/**
 * Gets a safe same-origin relative return URL from the location search params.
 * Fail-closed: rejects protocol-relative, scheme-bearing, backslash, root, and
 * empty values. Callers should fall back to role-based redirects when null.
 */
export function getReturnUrl(): string | null {
  const urlParams = new URLSearchParams(location.search);
  const returnUrl = urlParams.get("returnUrl");
  if (!returnUrl) {
    return null;
  }

  let decodedReturnUrl: string;
  try {
    decodedReturnUrl = decodeURIComponent(returnUrl);
  } catch {
    return null;
  }

  if (!isSafeReturnPath(decodedReturnUrl)) {
    return null;
  }

  return decodedReturnUrl;
}

/** Same-origin relative path only — no schemes, backslashes, or protocol-relative URLs. */
export function isSafeReturnPath(path: string): boolean {
  if (!path || path === "/") {
    return false;
  }

  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return false;
  }

  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) {
    return false;
  }

  // Reject encoded traversal and nested absolute URLs after a single decode.
  const lowered = path.toLowerCase();
  if (
    lowered.includes("://") ||
    lowered.includes("%2f%2f") ||
    lowered.includes("%5c")
  ) {
    return false;
  }

  return true;
}
